import { useEffect } from 'react';
import { useLocation } from 'react-router';

/**
 * Asks before a page with unsaved changes is left (plan D53). The app mounts `<BrowserRouter>` (main.tsx), not a data
 * router, so react-router's `useBlocker` is unavailable. A capture listener on the document runs before React's root
 * listener, and react-router's `<Link>` skips its navigation when the click's `defaultPrevented` is set, so refusing
 * the confirm keeps the route. A reload or a closed tab gets the browser's own `beforeunload` question.
 * Residual: browser back/forward and a programmatic `navigate()` (the ⌘K palette) are not intercepted; a page's own
 * back button asks through `confirmLeave()`.
 *
 * S8 §9 (D87): several drafts on one page (`/assistant/me`) each call the hook; the listeners are installed once while
 * any of them is dirty, so leaving asks once. A link to the same page (another tab, an anchor) is not a leave: its
 * drafts stay mounted, so nothing asks.
 */
export const LEAVE_MESSAGE = 'Có thay đổi chưa lưu. Rời trang?';

let dirtyCount = 0;
/** The router's path of the page holding the drafts (not `window.location`: the router owns the address). */
let guardedPath = '';

/** Whether any mounted draft is unsaved. */
export const hasUnsavedChanges = (): boolean => dirtyCount > 0;

/** For a programmatic leave (a back button): `true` when nothing is unsaved or the person agrees to leave. */
export const confirmLeave = (): boolean => !hasUnsavedChanges() || window.confirm(LEAVE_MESSAGE);

const onClick = (event: MouseEvent) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey
    || event.altKey) return;
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!link || (link.getAttribute('target') ?? '_self') !== '_self') return;
  const to = new URL(link.getAttribute('href') ?? '', window.location.href);
  if (to.origin === window.location.origin && to.pathname === guardedPath) return;
  if (!window.confirm(LEAVE_MESSAGE)) { event.preventDefault(); event.stopPropagation(); }
};
const onUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };

export function useLeaveGuard(dirty: boolean): void {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!dirty) return undefined;
    guardedPath = pathname;
    dirtyCount += 1;
    if (dirtyCount === 1) {
      document.addEventListener('click', onClick, true);
      window.addEventListener('beforeunload', onUnload);
    }
    return () => {
      dirtyCount -= 1;
      if (dirtyCount === 0) {
        document.removeEventListener('click', onClick, true);
        window.removeEventListener('beforeunload', onUnload);
      }
    };
  }, [dirty, pathname]);
}
